import {
  ArrowLeft,
  Building2,
  CheckCircle2,
  Clock,
  ExternalLink,
  Gavel,
  Hash,
  Mail,
  MessageSquare,
  Phone,
  Plus,
  Send,
  ShieldCheck,
  Trash2,
  User,
  XCircle,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";

const API = import.meta.env.VITE_API_URL || "http://localhost:9000";

type Comment = {
  id: string;
  body: string;
  createdAt: string;
  userId: string;
  userName: string;
  userEmail: string | null;
};

type AuctionEntity = {
  id: string;
  title: string;
  status: string;
  reviewStatus: string;
  currentBid: number;
  bidCount: number;
  endsAt: string | null;
};

type RegistrationEntity = {
  id: string;
  name: string;
  orgNumber: string;
  email: string;
  phone: string;
  status: string;
  onboardingStatus?: string;
  onboardingError?: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
};

type BankAccountEntity = {
  id: string;
  name: string;
  orgNumber: string | null;
  email: string | null;
  phone: string | null;
  bankAccountNumber: string | null;
  pendingBankAccountNumber: string | null;
};

type ManualSignatory = {
  name: string;
  email: string;
};

type RegistrationSignatory = {
  name: string;
  birthDate: string | null;
  roles: string[];
  roleCodes: string[];
  combinationTexts: string[];
  combinationCodes?: string[];
};

type RegistrationSignatoryLookup = {
  status?: string;
  signingText?: string | null;
  combinations?: string[];
  combinationCodes?: string[];
  companyStatus?: {
    konkurs?: boolean | null;
    underAvvikling?: boolean | null;
    underTvangsavviklingEllerTvangsopplosning?: boolean | null;
  };
};

type TaskDetail = {
  id: string;
  module: string;
  entityType: string;
  entityId: string;
  taskType: string;
  title: string;
  description: string | null;
  status: string;
  priority: string;
  metadata: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
  organization: { id: string; name: string; slug: string } | null;
  createdByUser: {
    id: string;
    name: string;
    email: string | null;
    firstName: string | null;
    lastName: string | null;
    phoneNumber: string | null;
  } | null;
  latestSession: {
    id: string;
    ipAddress: string | null;
    userAgent: string | null;
    activeOrganizationId: string | null;
    impersonatedBy: string | null;
    createdAt: string;
    updatedAt: string;
    expiresAt: string;
  } | null;
  comments: Comment[];
  entity: AuctionEntity | RegistrationEntity | BankAccountEntity | null;
};

function isAuctionEntity(entity: TaskDetail["entity"]): entity is AuctionEntity {
  return Boolean(entity && "reviewStatus" in entity);
}

function isRegistrationEntity(entity: TaskDetail["entity"]): entity is RegistrationEntity {
  return Boolean(entity && "orgNumber" in entity);
}

function isBankAccountEntity(entity: TaskDetail["entity"]): entity is BankAccountEntity {
  return Boolean(entity && "pendingBankAccountNumber" in entity && !("metadata" in entity));
}

function getRegistrationContactName(metadata: RegistrationEntity["metadata"]) {
  if (!metadata) return null;
  const fullName = metadata.fullName;
  return typeof fullName === "string" && fullName.trim() ? fullName : null;
}

function getRegistrationSignatories(metadata: RegistrationEntity["metadata"]) {
  if (!metadata || !Array.isArray(metadata.signatories)) return [];
  return metadata.signatories.filter(isRegistrationSignatory);
}

function getRegistrationSignatoryLookup(metadata: RegistrationEntity["metadata"]) {
  if (!metadata) return null;
  const lookup = metadata.signatoryLookup;
  return isRegistrationSignatoryLookup(lookup) ? lookup : null;
}

function getManualRegistrationSignatories(metadata: RegistrationEntity["metadata"]) {
  if (!metadata || !Array.isArray(metadata.manualSignatories)) return [];
  return metadata.manualSignatories
    .map((value) => {
      if (!value || typeof value !== "object" || Array.isArray(value)) return null;
      const record = value as Record<string, unknown>;
      return {
        name: typeof record.name === "string" ? record.name : "",
        email: typeof record.email === "string" ? record.email : "",
      };
    })
    .filter((value): value is ManualSignatory => Boolean(value && value.name.trim() && value.email.trim()));
}

function isRegistrationSignatory(value: unknown): value is RegistrationSignatory {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const record = value as Record<string, unknown>;
  return typeof record.name === "string" && Array.isArray(record.roles) && Array.isArray(record.roleCodes) && Array.isArray(record.combinationTexts);
}

function isRegistrationSignatoryLookup(value: unknown): value is RegistrationSignatoryLookup {
  return Boolean(value && typeof value === "object" && !Array.isArray(value));
}

function formatBooleanStatus(value: boolean | null | undefined) {
  if (value === true) return { label: "Ja", variant: "destructive" as const };
  if (value === false) return { label: "Nei", variant: "success" as const };
  return { label: "Ukjent", variant: "muted" as const };
}

function getTaskMetadataValue(metadata: Record<string, unknown> | null, key: string) {
  const value = metadata?.[key];
  return typeof value === "string" && value.trim() ? value : null;
}

function getSubmittedBankAccountFromDescription(description: string | null) {
  if (!description) return null;
  const match = description.match(/Innsendt kontonummer:\s*(.+)$/i);
  return match?.[1]?.trim() || null;
}

export function OppgaveDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<TaskDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState("");
  const [sending, setSending] = useState(false);
  const [acting, setActing] = useState(false);
  const [manualSignatories, setManualSignatories] = useState<ManualSignatory[]>([{ name: "", email: "" }]);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!id) return;
    fetch(`${API}/api/admin/tasks/${id}`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then(setData)
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (!data || !isRegistrationEntity(data.entity)) {
      return;
    }

    const existingManualSignatories = getManualRegistrationSignatories(data.entity.metadata);
    if (existingManualSignatories.length > 0) {
      setManualSignatories(existingManualSignatories);
      return;
    }

    const fallbackName = getRegistrationContactName(data.entity.metadata) ?? "";
    const fallbackEmail = data.entity.email ?? "";
    setManualSignatories([{ name: fallbackName, email: fallbackEmail }]);
  }, [data]);

  async function sendComment() {
    if (!id || !comment.trim()) return;
    setSending(true);
    try {
      const r = await fetch(`${API}/api/admin/tasks/${id}/comments`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ body: comment.trim() }),
      });
      if (r.ok) {
        const newComment = await r.json();
        setData((d) => d ? { ...d, comments: [...d.comments, newComment] } : d);
        setComment("");
        setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
      }
    } finally {
      setSending(false);
    }
  }

  async function updateStatus(status: string) {
    if (!id) return;
    setActing(true);
    try {
      const r = await fetch(`${API}/api/admin/tasks/${id}/status`, {
        method: "PATCH", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      });
      if (r.ok) setData((d) => d ? { ...d, status } : d);
    } finally {
      setActing(false);
    }
  }

  async function resolveAuctionReview(decision: "approved" | "changes_requested") {
    if (!id) return;
    setActing(true);
    try {
      const r = await fetch(`${API}/api/admin/tasks/${id}/resolve-auction-review`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ decision, comment: comment.trim() || undefined }),
      });
      if (r.ok) {
        const result = await r.json();
        setData((d) => d ? {
          ...d,
          status: result.task.status,
          metadata: result.task.metadata,
          entity: d.entity ? { ...d.entity, reviewStatus: result.auction.reviewStatus } : null,
        } : d);
        setComment("");
      }
    } finally {
      setActing(false);
    }
  }

  async function resolveRegistration(decision: "approved" | "rejected") {
    if (!id) return;
    setActing(true);
    try {
      const signatoriesPayload = decision === "approved"
        ? manualSignatories
          .map((signatory) => ({
            name: signatory.name.trim(),
            email: signatory.email.trim(),
          }))
          .filter((signatory) => signatory.name && signatory.email)
        : undefined;

      const r = await fetch(`${API}/api/admin/tasks/${id}/resolve-registration`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ decision, comment: comment.trim() || undefined, signatories: signatoriesPayload }),
      });
      if (r.ok) {
        const result = await r.json();
        setData((d) => d ? {
          ...d,
          status: result.task.status,
          metadata: result.task.metadata,
          entity: d.entity
            ? {
                ...d.entity,
                status: result.registration.status,
                onboardingStatus: result.registration.onboardingStatus,
                onboardingError: result.registration.onboardingError,
                metadata: result.registration.metadata,
              }
            : null,
        } : d);
        setComment("");
      }
    } finally {
      setActing(false);
    }
  }

  async function resolveBankAccount(decision: "approved" | "rejected") {
    if (!id) return;
    setActing(true);
    try {
      const r = await fetch(`${API}/api/admin/tasks/${id}/resolve-bank-account`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ decision, comment: comment.trim() || undefined }),
      });
      if (r.ok) {
        const result = await r.json();
        setData((d) => d ? {
          ...d,
          status: result.task.status,
          metadata: result.task.metadata,
          entity: d.entity && isBankAccountEntity(d.entity)
            ? {
                ...d.entity,
                bankAccountNumber: result.organization.bankAccountNumber,
                pendingBankAccountNumber: result.organization.pendingBankAccountNumber,
              }
            : d.entity,
        } : d);
        setComment("");
      }
    } finally {
      setActing(false);
    }
  }

  // Redirect auction review tasks straight to the auction detail page
  useEffect(() => {
    if (data?.taskType === "approval_review" && data.module === "auctions" && data.entityId) {
      navigate(`/auksjoner/${data.entityId}`, { replace: true });
    }
  }, [data, navigate]);

  if (loading) return <div className="px-4 py-6 sm:px-6 lg:px-8 max-w-screen-xl mx-auto"><p className="text-sm text-muted-foreground">Laster oppgave...</p></div>;
  if (!data) return <div className="px-4 py-6 sm:px-6 lg:px-8 max-w-screen-xl mx-auto"><p className="text-sm text-destructive">Oppgaven ble ikke funnet.</p></div>;

  const isAuctionReview = data.taskType === "approval_review" && data.module === "auctions";
  const isRegistrationTask = data.taskType === "org_registration" && data.module === "registrations";
  const isBankAccountTask = data.taskType === "bank_account_approval";
  const isOpen = data.status === "open";
  const taskOrganization = data.organization;
  const registrationContactName = isRegistrationEntity(data.entity) ? getRegistrationContactName(data.entity.metadata) : null;
  const registrationSignatories = isRegistrationEntity(data.entity) ? getRegistrationSignatories(data.entity.metadata) : [];
  const registrationSignatoryLookup = isRegistrationEntity(data.entity) ? getRegistrationSignatoryLookup(data.entity.metadata) : null;
  const hasValidManualSignatories =
    manualSignatories.length > 0 &&
    manualSignatories.every((signatory) => signatory.name.trim().length > 0 && signatory.email.trim().length > 0);
  const bankEntity = isBankAccountEntity(data.entity) ? data.entity : null;
  const submittedBankAccount =
    bankEntity?.pendingBankAccountNumber ??
    getTaskMetadataValue(data.metadata, "submittedBankAccountNumber") ??
    getSubmittedBankAccountFromDescription(data.description);
  const currentBankAccount =
    bankEntity?.bankAccountNumber ??
    getTaskMetadataValue(data.metadata, "previousBankAccountNumber");
  const auditIp =
    getTaskMetadataValue(data.metadata, "requestIp") ??
    getTaskMetadataValue(data.metadata, "sessionIpAddress") ??
    data.latestSession?.ipAddress ??
    null;
  const auditUserAgent =
    getTaskMetadataValue(data.metadata, "requestUserAgent") ??
    getTaskMetadataValue(data.metadata, "sessionUserAgent") ??
    data.latestSession?.userAgent ??
    null;
  const auditSubmittedAt =
    getTaskMetadataValue(data.metadata, "submittedAt") ??
    null;
  const auditSessionUpdatedAt =
    getTaskMetadataValue(data.metadata, "sessionUpdatedAt") ??
    data.latestSession?.updatedAt ??
    null;
  const auditSessionExpiresAt =
    getTaskMetadataValue(data.metadata, "sessionExpiresAt") ??
    data.latestSession?.expiresAt ??
    null;
  const auditImpersonatedBy =
    getTaskMetadataValue(data.metadata, "impersonatedBy") ??
    data.latestSession?.impersonatedBy ??
    null;
  const auditSessionId = getTaskMetadataValue(data.metadata, "sessionId");
  const entityLink = data.entityType === "auction" ? `/auksjoner/${data.entityId}`
    : data.entityType === "organization" ? `/organisasjoner/${data.entityId}`
    : data.entityType === "bank_account_request" ? data.organization ? `/organisasjoner/${data.organization.id}` : null
    : data.entityType === "user" ? `/brukere/${data.entityId}` : null;

  return (
    <div className="px-4 py-6 sm:px-6 lg:px-8 max-w-screen-xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-start gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate("/oppgaver")} className="mt-0.5 flex-shrink-0">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-black tracking-tight" style={{ fontFamily: "var(--font-display)" }}>
              {data.title}
            </h1>
            <StatusBadge status={data.status} />
            <PriorityBadge priority={data.priority} />
          </div>
          {data.organization ? (
            <button
              className="text-sm text-muted-foreground hover:text-foreground transition-colors mt-0.5"
              onClick={() => navigate(`/organisasjoner/${data.organization!.id}`)}
            >
              {data.organization.name}
            </button>
          ) : (
            <span className="text-sm text-muted-foreground mt-0.5">Ikke tilknyttet organisasjon</span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left — comments + description */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          {data.description && (
            <Card>
              <CardHeader><CardTitle className="text-sm font-semibold">Beskrivelse</CardTitle></CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">{data.description}</p>
              </CardContent>
            </Card>
          )}

          {/* Entity snapshot */}
          {data.entity && isAuctionReview && isAuctionEntity(data.entity) && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-sm font-semibold">Auksjon</CardTitle>
                {entityLink && (
                  <Button variant="ghost" size="sm" className="h-7 text-xs gap-1.5 text-muted-foreground" onClick={() => navigate(entityLink)}>
                    Åpne <ExternalLink className="h-3 w-3" />
                  </Button>
                )}
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Tittel</span>
                  <span className="font-medium">{data.entity.title}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Status</span>
                  <AuctionReviewBadge reviewStatus={data.entity.reviewStatus} />
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Høyeste bud</span>
                  <span>{data.entity.currentBid > 0 ? `${data.entity.currentBid.toLocaleString("nb-NO")} kr` : "—"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Antall bud</span>
                  <span>{data.entity.bidCount}</span>
                </div>
                {data.entity.endsAt && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Slutter</span>
                    <span>{formatDateTime(data.entity.endsAt)}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Registration snapshot */}
          {data.entity && isRegistrationTask && isRegistrationEntity(data.entity) && (() => {
            const reg = data.entity;
            return (
              <Card className="border-border/80">
                <CardHeader>
                  <CardTitle className="text-sm font-semibold">Registreringsdetaljer</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5" />Selskapsnavn</span>
                    <span className="font-medium">{reg.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground flex items-center gap-1.5"><Hash className="h-3.5 w-3.5" />Org.nr</span>
                    <span className="font-mono">{reg.orgNumber}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" />E-post</span>
                    <a href={`mailto:${reg.email}`} className="text-primary hover:underline">{reg.email}</a>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" />Telefon</span>
                    <span>{reg.phone}</span>
                  </div>
                  {registrationContactName && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground flex items-center gap-1.5"><User className="h-3.5 w-3.5" />Kontaktperson</span>
                      <span>{registrationContactName}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Status</span>
                    <RegistrationStatusBadge status={reg.status} />
                  </div>
                  {reg.onboardingStatus && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Onboarding</span>
                      <RegistrationOnboardingBadge status={reg.onboardingStatus} />
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Innsendt</span>
                    <span>{formatDateTime(reg.createdAt)}</span>
                  </div>
                  {reg.onboardingError && (
                    <div className="rounded-lg border border-destructive/20 bg-destructive/5 px-3 py-2 text-xs text-destructive">
                      {reg.onboardingError}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })()}

          {isBankAccountTask && (
            <Card className="border-border/80">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-sm font-semibold">Kontonummer</CardTitle>
                {entityLink && (
                  <Button variant="ghost" size="sm" className="h-7 text-xs gap-1.5 text-muted-foreground" onClick={() => navigate(entityLink)}>
                    Åpne <ExternalLink className="h-3 w-3" />
                  </Button>
                )}
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5" />Selskapsnavn</span>
                  <span className="font-medium">{bankEntity?.name ?? taskOrganization?.name ?? "—"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground flex items-center gap-1.5"><Hash className="h-3.5 w-3.5" />Org.nr</span>
                  <span className="font-mono">{bankEntity?.orgNumber ?? "—"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Aktivt kontonummer</span>
                  <span className="font-mono">{currentBankAccount ?? "Ikke registrert"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Innsendt kontonummer</span>
                  <span className="font-mono">{submittedBankAccount ?? "Ingen ventende endring"}</span>
                </div>
              </CardContent>
            </Card>
          )}

          {isBankAccountTask && (
            <Card className="border-border/80">
              <CardHeader>
                <CardTitle className="text-sm font-semibold">Revisjonsspor</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex justify-between gap-4">
                  <span className="text-muted-foreground">Bruker</span>
                  <span className="text-right font-medium">
                    {data.createdByUser?.name ?? data.createdByUser?.email ?? "Ukjent"}
                  </span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-muted-foreground">E-post</span>
                  <span className="text-right">{data.createdByUser?.email ?? "—"}</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-muted-foreground">Telefon</span>
                  <span className="text-right">{data.createdByUser?.phoneNumber ?? "—"}</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-muted-foreground">Innsendt</span>
                  <span className="text-right">{auditSubmittedAt ? formatDateTime(auditSubmittedAt) : formatDateTime(data.createdAt)}</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-muted-foreground">IP-adresse</span>
                  <span className="text-right font-mono">{auditIp ?? "—"}</span>
                </div>
                <div className="space-y-1 pt-1">
                  <span className="text-muted-foreground">User-Agent</span>
                  <p className="rounded-lg bg-muted/40 px-3 py-2 text-xs break-words">
                    {auditUserAgent ?? "—"}
                  </p>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-muted-foreground">Sesjon-ID</span>
                  <span className="text-right font-mono">{auditSessionId ?? "—"}</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-muted-foreground">Sesjon oppdatert</span>
                  <span className="text-right">{auditSessionUpdatedAt ? formatDateTime(auditSessionUpdatedAt) : "—"}</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-muted-foreground">Sesjon utløper</span>
                  <span className="text-right">{auditSessionExpiresAt ? formatDateTime(auditSessionExpiresAt) : "—"}</span>
                </div>
                {auditImpersonatedBy && (
                  <div className="flex justify-between gap-4">
                    <span className="text-muted-foreground">Impersonert av</span>
                    <span className="text-right font-mono">{auditImpersonatedBy}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {isRegistrationTask && (
            <Card className="border-primary/10 bg-[linear-gradient(180deg,rgba(15,23,42,0.015),transparent)]">
              <CardHeader>
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-primary" />
                  Signaturrett
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {registrationSignatoryLookup?.companyStatus && (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    {(() => {
                      const status = formatBooleanStatus(registrationSignatoryLookup.companyStatus.konkurs);
                      return <StatusPill title="Konkurs" value={status.label} variant={status.variant} />;
                    })()}
                    {(() => {
                      const status = formatBooleanStatus(registrationSignatoryLookup.companyStatus.underAvvikling);
                      return <StatusPill title="Under avvikling" value={status.label} variant={status.variant} />;
                    })()}
                    {(() => {
                      const status = formatBooleanStatus(registrationSignatoryLookup.companyStatus.underTvangsavviklingEllerTvangsopplosning);
                      return <StatusPill title="Tvangsavvikling" value={status.label} variant={status.variant} />;
                    })()}
                  </div>
                )}

                {registrationSignatoryLookup?.signingText && (
                  <div className="rounded-xl border border-border bg-muted/30 px-4 py-3">
                    <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Signeringsgrunnlag</p>
                    <p className="mt-1 text-sm font-medium">{registrationSignatoryLookup.signingText}</p>
                  </div>
                )}

                {registrationSignatories.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Ingen signaturdata hentet ennå.</p>
                ) : (
                  <div className="space-y-3">
                    {registrationSignatories.map((signatory) => (
                      <div key={`${signatory.name}-${signatory.birthDate ?? "unknown"}`} className="rounded-2xl border border-border bg-background p-4 shadow-sm">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="text-sm font-semibold">{signatory.name}</p>
                            {signatory.birthDate && (
                              <p className="text-xs text-muted-foreground">Fødselsdato: {signatory.birthDate}</p>
                            )}
                          </div>
                          <Badge variant="accent">{signatory.roleCodes.join(" / ") || "Ingen kode"}</Badge>
                        </div>
                        <div className="mt-3 flex flex-wrap gap-1.5">
                          {signatory.roles.map((role) => (
                            <Badge key={role} variant="secondary">{role}</Badge>
                          ))}
                        </div>
                        {signatory.combinationTexts.length > 0 && (
                          <div className="mt-3 rounded-xl bg-muted/30 px-3 py-2">
                            <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Kombinasjoner</p>
                            <div className="mt-2 space-y-1.5">
                              {signatory.combinationTexts.map((text, index) => (
                                <div key={`${text}-${signatory.combinationCodes?.[index] ?? index}`} className="flex items-center justify-between gap-3 text-sm">
                                  <span>{text}</span>
                                  {signatory.combinationCodes?.[index] && (
                                    <span className="rounded-md border border-border bg-background px-2 py-0.5 font-mono text-[11px] text-muted-foreground">
                                      {signatory.combinationCodes[index]}
                                    </span>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Comments */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <MessageSquare className="h-4 w-4" />
                Kommentarer ({data.comments.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {data.comments.length === 0 ? (
                <p className="text-sm text-muted-foreground">Ingen kommentarer ennå.</p>
              ) : (
                <div className="space-y-4">
                  {data.comments.map((c) => (
                    <div key={c.id} className="flex gap-3">
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/8 flex-shrink-0 mt-0.5">
                        <span className="text-xs font-bold text-primary">{c.userName[0]?.toUpperCase()}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-2">
                          <span className="text-sm font-medium">{c.userName}</span>
                          <span className="text-xs text-muted-foreground">{formatDateTime(c.createdAt)}</span>
                        </div>
                        <p className="text-sm text-foreground mt-0.5 whitespace-pre-wrap">{c.body}</p>
                      </div>
                    </div>
                  ))}
                  <div ref={bottomRef} />
                </div>
              )}

              {/* Comment input */}
              <div className="pt-3 border-t border-border space-y-2">
                <textarea
                  className="w-full min-h-[80px] rounded-lg border border-border bg-background px-3 py-2 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="Skriv en kommentar..."
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) sendComment();
                  }}
                />
                <div className="flex justify-end">
                  <Button size="sm" className="gap-2" disabled={sending || !comment.trim()} onClick={sendComment}>
                    <Send className="h-3.5 w-3.5" />
                    {sending ? "Sender..." : "Send kommentar"}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right sidebar */}
        <div className="space-y-4">
          {isRegistrationTask && isOpen && (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-semibold">Signatarer for kontrakt</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-xs text-muted-foreground">
                  Legg inn hvem som faktisk skal motta og signere avtalen i Idura. Minst én signatar er påkrevd.
                </p>
                {manualSignatories.map((signatory, index) => (
                  <div key={`manual-signatory-${index}`} className="rounded-xl border border-border bg-muted/20 p-3 space-y-3">
                    <div className="grid gap-3 sm:grid-cols-2">
                      <label className="space-y-1">
                        <span className="text-xs font-medium text-muted-foreground">Navn</span>
                        <input
                          className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                          value={signatory.name}
                          onChange={(e) => {
                            const value = e.target.value;
                            setManualSignatories((current) =>
                              current.map((item, currentIndex) =>
                                currentIndex === index ? { ...item, name: value } : item,
                              ),
                            );
                          }}
                          placeholder="Ola Nordmann"
                        />
                      </label>
                      <label className="space-y-1">
                        <span className="text-xs font-medium text-muted-foreground">E-post</span>
                        <input
                          type="email"
                          className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                          value={signatory.email}
                          onChange={(e) => {
                            const value = e.target.value;
                            setManualSignatories((current) =>
                              current.map((item, currentIndex) =>
                                currentIndex === index ? { ...item, email: value } : item,
                              ),
                            );
                          }}
                          placeholder="navn@firma.no"
                        />
                      </label>
                    </div>
                    <div className="flex justify-end">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="gap-2 text-muted-foreground"
                        disabled={manualSignatories.length === 1}
                        onClick={() => {
                          setManualSignatories((current) =>
                            current.length === 1 ? current : current.filter((_, currentIndex) => currentIndex !== index),
                          );
                        }}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        Fjern
                      </Button>
                    </div>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  className="w-full gap-2"
                  onClick={() => setManualSignatories((current) => [...current, { name: "", email: "" }])}
                >
                  <Plus className="h-4 w-4" />
                  Legg til signatar
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Actions */}
          <Card>
            <CardHeader><CardTitle className="text-sm font-semibold">Handlinger</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {isAuctionReview && isOpen && (
                <>
                  <p className="text-xs text-muted-foreground pb-1">
                    Kommentaren (om utfylt) sendes med beslutningen.
                  </p>
                  <Button className="w-full gap-2" disabled={acting} onClick={() => resolveAuctionReview("approved")}>
                    <CheckCircle2 className="h-4 w-4" /> Godkjenn auksjon
                  </Button>
                  <Button variant="outline" className="w-full gap-2" disabled={acting} onClick={() => resolveAuctionReview("changes_requested")}>
                    <XCircle className="h-4 w-4" /> Send tilbake
                  </Button>
                </>
              )}
              {isRegistrationTask && isOpen && (
                <>
                  <p className="text-xs text-muted-foreground pb-1">
                    Kommentaren sendes med beslutningen. Registrering kan ikke godkjennes uten minst én signatar med navn og e-post.
                  </p>
                  <Button className="w-full gap-2" disabled={acting || !hasValidManualSignatories} onClick={() => resolveRegistration("approved")}>
                    <CheckCircle2 className="h-4 w-4" /> Godkjenn registrering
                  </Button>
                  <Button variant="destructive" className="w-full gap-2" disabled={acting} onClick={() => resolveRegistration("rejected")}>
                    <XCircle className="h-4 w-4" /> Avvis registrering
                  </Button>
                </>
              )}
              {isBankAccountTask && isOpen && (
                <>
                  <p className="text-xs text-muted-foreground pb-1">
                    Kommentaren (om utfylt) logges på oppgaven sammen med beslutningen.
                  </p>
                  <Button className="w-full gap-2" disabled={acting} onClick={() => resolveBankAccount("approved")}>
                    <CheckCircle2 className="h-4 w-4" /> Godkjenn kontonummer
                  </Button>
                  <Button variant="destructive" className="w-full gap-2" disabled={acting} onClick={() => resolveBankAccount("rejected")}>
                    <XCircle className="h-4 w-4" /> Avvis kontonummer
                  </Button>
                </>
              )}
              {isOpen && !isAuctionReview && !isRegistrationTask && !isBankAccountTask && (
                <Button className="w-full gap-2" disabled={acting} onClick={() => updateStatus("closed")}>
                  <CheckCircle2 className="h-4 w-4" /> {acting ? "Lukker..." : "Merk som løst"}
                </Button>
              )}
              {!isOpen && (
                <Button variant="outline" className="w-full gap-2" disabled={acting} onClick={() => updateStatus("open")}>
                  <Clock className="h-4 w-4" /> Gjenåpne oppgave
                </Button>
              )}
              {entityLink && (
                <Button variant="ghost" className="w-full gap-2 text-muted-foreground" onClick={() => navigate(entityLink)}>
                  <ExternalLink className="h-4 w-4" /> Gå til {entityLabel(data.entityType)}
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Meta */}
          <Card>
            <CardHeader><CardTitle className="text-sm font-semibold">Detaljer</CardTitle></CardHeader>
            <CardContent className="space-y-2 text-sm">
              <MetaRow label="Type"><TypeBadge taskType={data.taskType} module={data.module} /></MetaRow>
              <MetaRow label="Modul"><span className="font-mono text-xs">{data.module}</span></MetaRow>
              <MetaRow label="Entitet"><span className="font-mono text-xs">{data.entityType}</span></MetaRow>
              <MetaRow label="Opprettet">{formatDateTime(data.createdAt)}</MetaRow>
              <MetaRow label="Oppdatert">{formatDateTime(data.updatedAt)}</MetaRow>
              <MetaRow label="Organisasjon">
                {taskOrganization ? (
                  <button className="text-primary hover:underline text-left" onClick={() => navigate(`/organisasjoner/${taskOrganization.id}`)}>
                    {taskOrganization.name}
                  </button>
                ) : (
                  <span className="text-muted-foreground">Ikke tilknyttet organisasjon</span>
                )}
              </MetaRow>
            </CardContent>
          </Card>

          {/* Metadata */}
          {data.metadata && Object.keys(data.metadata).length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-sm font-semibold">Metadata</CardTitle></CardHeader>
              <CardContent>
                <details className="group rounded-xl border border-border bg-muted/20">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-3 text-sm font-medium marker:hidden">
                    <span>Vis rå metadata</span>
                    <span className="text-xs text-muted-foreground transition-transform group-open:rotate-180">⌄</span>
                  </summary>
                  <div className="border-t border-border px-4 py-3">
                    <pre className="text-xs text-muted-foreground overflow-x-auto whitespace-pre-wrap break-all">
                      {JSON.stringify(data.metadata, null, 2)}
                    </pre>
                  </div>
                </details>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

function MetaRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <span className="text-muted-foreground flex-shrink-0">{label}</span>
      <span className="text-right">{children}</span>
    </div>
  );
}

function StatusPill({
  title,
  value,
  variant,
}: {
  title: string;
  value: string;
  variant: "success" | "destructive" | "muted";
}) {
  return (
    <div className="rounded-xl border border-border bg-background px-3 py-3">
      <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">{title}</p>
      <div className="mt-2">
        <Badge variant={variant}>{value}</Badge>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  if (status === "open") return <Badge variant="warning">Åpen</Badge>;
  if (status === "closed") return <Badge variant="success">Lukket</Badge>;
  return <Badge variant="muted">{status}</Badge>;
}

function PriorityBadge({ priority }: { priority: string }) {
  const map: Record<string, { label: string; variant: any }> = {
    high: { label: "Høy prioritet", variant: "destructive" },
    medium: { label: "Medium", variant: "warning" },
    normal: { label: "Normal", variant: "muted" },
    low: { label: "Lav", variant: "muted" },
  };
  const { label, variant } = map[priority] ?? { label: priority, variant: "muted" };
  return <Badge variant={variant}>{label}</Badge>;
}

function TypeBadge({ taskType, module: mod }: { taskType: string; module: string }) {
  if (taskType === "approval_review" && mod === "auctions") return <Badge variant="secondary">Auksjonsgodkjenning</Badge>;
  if (taskType === "org_registration") return <Badge variant="warning">Ny registrering</Badge>;
  if (taskType === "org_registration_duplicate") return <Badge variant="secondary">Allerede kunde</Badge>;
  if (taskType === "bank_account_approval") return <Badge variant="secondary">Kontonummer</Badge>;
  return <Badge variant="secondary">{taskType}</Badge>;
}

function AuctionReviewBadge({ reviewStatus }: { reviewStatus: string }) {
  if (reviewStatus === "submitted") return <Badge variant="warning">Til godkjenning</Badge>;
  if (reviewStatus === "approved") return <Badge variant="success">Godkjent</Badge>;
  if (reviewStatus === "changes_requested") return <Badge variant="destructive">Endringer ønsket</Badge>;
  return <Badge variant="muted">{reviewStatus}</Badge>;
}

function RegistrationStatusBadge({ status }: { status: string }) {
  if (status === "pending") return <Badge variant="warning">Venter</Badge>;
  if (status === "approved") return <Badge variant="success">Godkjent</Badge>;
  if (status === "rejected") return <Badge variant="destructive">Avvist</Badge>;
  return <Badge variant="muted">{status}</Badge>;
}

function RegistrationOnboardingBadge({ status }: { status: string }) {
  if (status === "organization_created") return <Badge variant="secondary">Organisasjon opprettet</Badge>;
  if (status === "document_queued") return <Badge variant="warning">Kontrakt køet</Badge>;
  if (status === "awaiting_signature") return <Badge variant="warning">Venter på signering</Badge>;
  if (status === "completed") return <Badge variant="success">Fullført</Badge>;
  if (status === "failed") return <Badge variant="destructive">Feilet</Badge>;
  if (status === "rejected") return <Badge variant="destructive">Avvist</Badge>;
  return <Badge variant="muted">{status}</Badge>;
}

function entityLabel(entityType: string) {
  const map: Record<string, string> = { auction: "auksjon", organization: "organisasjon", user: "bruker", bank_account_request: "organisasjon" };
  return map[entityType] ?? entityType;
}

function formatDateTime(iso: string) {
  return new Date(iso).toLocaleString("nb-NO", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}
